/**
 * LR HUB identity helpers — initials avatar generation + display name / handle rules.
 *
 * ISOLATION: everything here is LR Hub only. It reads nothing from and
 * writes nothing to profiles / companies / billing.
 *
 * IDENTITY MODEL:
 *  - display_name — what shows on posts. Spaces/capitals allowed, 2–30 chars, NOT unique.
 *  - handle — the unique @handle. lowercase letters/numbers/underscores, 3–20 chars.
 */

export const HUB_HANDLE_RE = /^[a-z0-9_]{3,20}$/;

const RESERVED_SUBSTRINGS = ["lanerunner", "admin", "official"];
const RESERVED_EXACT = ["support", "moderator", "lr", "lrdirect"];

/** Trim + collapse internal whitespace (mirror of the DB guard). */
export function normalizeHubDisplayName(raw: string): string {
  return raw.replace(/\s+/g, " ").trim();
}

/** Client-side mirror of the DB display-name rules. Returns null when valid. */
export function validateHubDisplayName(raw: string): string | null {
  const n = normalizeHubDisplayName(raw);
  if (!n) return "Display name is required.";
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F\u007F]/.test(n)) return "Display name contains invalid characters.";
  if (n.length < 2 || n.length > 30) return "2–30 characters.";
  return null;
}

/**
 * Client-side mirror of the DB handle guard. Returns null when valid.
 *
 * `allowReserved` mirrors the server-side exemption in `hub_settings_guard` /
 * `hub_handle_available`: LaneRunner admins and the founder account may use
 * handles containing otherwise-reserved words (e.g. "lanerunner"). Regular
 * members are still blocked here AND in the database.
 */
export function validateHubHandle(raw: string, allowReserved = false): string | null {
  const h = raw.trim().toLowerCase();
  if (!h) return "Handle is required.";
  if (!HUB_HANDLE_RE.test(h)) {
    return "3–20 characters. Lowercase letters, numbers and underscores. No spaces.";
  }
  if (
    !allowReserved &&
    (RESERVED_SUBSTRINGS.some((s) => h.includes(s)) || RESERVED_EXACT.includes(h))
  ) {
    return "That handle is reserved. Please choose another.";
  }
  return null;
}


/** Suggests a handle from a display name (lowercase, spaces removed). */
export function suggestHubHandle(displayName: string): string {
  const base = normalizeHubDisplayName(displayName)
    .toLowerCase()
    .replace(/[^a-z0-9_ ]/g, "")
    .replace(/\s+/g, "")
    .slice(0, 20);
  return base;
}

/** Initials from a username (or a full name fallback). */
export function hubInitials(source?: string | null): string {
  const s = (source || "").trim();
  if (!s) return "LR";
  const parts = s.split(/[\s_.-]+/).filter(Boolean);
  if (parts.length === 0) return "LR";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/**
 * Deterministic avatar color derived from the username so a member's
 * generated avatar is identical everywhere and across sessions.
 * Palette stays inside the brand family (teal-forward, professional).
 */
const HUB_AVATAR_PALETTE = [
  "#02857E", // brand teal deep
  "#0F766E",
  "#115E59",
  "#1D4ED8",
  "#1E3A8A",
  "#4338CA",
  "#7C3AED",
  "#B45309",
  "#9A3412",
  "#B91C1C",
  "#0E7490",
  "#3F6212",
];

export function hubAvatarColor(source?: string | null): string {
  const s = (source || "lanerunner").toLowerCase();
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  }
  return HUB_AVATAR_PALETTE[hash % HUB_AVATAR_PALETTE.length];
}

export type HubAvatarStatus = "initials" | "pending" | "approved" | "rejected";

export const HUB_AVATAR_STATUS_LABEL: Record<HubAvatarStatus, string> = {
  initials: "Using generated initials",
  pending: "Photo pending review",
  approved: "Photo approved",
  rejected: "Photo not approved",
};

/** 30-day handle change window. */
export const HANDLE_CHANGE_DAYS = 30;

export function nextHandleChangeDate(changedAt?: string | null): Date | null {
  if (!changedAt) return null;
  const d = new Date(changedAt);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + HANDLE_CHANGE_DAYS);
  return d;
}

export function canChangeHandle(changedAt?: string | null): boolean {
  const next = nextHandleChangeDate(changedAt);
  return !next || next.getTime() <= Date.now();
}

/** Email that is always exempt from the reserved-handle words (the founder). */
export const HUB_FOUNDER_EMAIL = "tim@lanerunner.com";

/**
 * Client mirror of `public.hub_handle_reserved_exempt(uuid)`. UI-only convenience —
 * the database guard is the real enforcement point.
 */
export function hubHandleReservedExempt(isAdmin: boolean, email?: string | null): boolean {
  return !!isAdmin || (email || "").trim().toLowerCase() === HUB_FOUNDER_EMAIL;
}
